import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { OrbData, BudgetItem, BudgetCategory } from '../types/FinancialData';

interface OrbVisualizationProps {
  data: BudgetItem[];
  categories: BudgetCategory[];
  selectedYears: string[];
  onOrbClick: (orb: OrbData) => void;
  width?: number;
  height?: number;
  maxAmount?: number;
}

export const OrbVisualization: React.FC<OrbVisualizationProps> = ({
  data,
  categories,
  selectedYears,
  onOrbClick,
  width = 900,
  height = 700,
  maxAmount = Infinity
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const simulationRef = useRef<d3.Simulation<d3.SimulationNodeDatum, undefined> | null>(null);
  const zoomRef = useRef<d3.ZoomTransform>(d3.zoomIdentity);
  const categoryPositionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());

  const [viewMode, setViewMode] = React.useState<'grouped' | 'projects' | 'ungrouped'>('grouped');
  const [selectedCategory, setSelectedCategory] = React.useState<string | undefined>();

  const getValueForItem = (item: BudgetItem): number => {
    if (selectedYears.length === 0) {
      return Object.values(item.yearlyAmounts).reduce((sum, v) => sum + v, 0);
    }
    return selectedYears.reduce((sum, yr) => sum + (item.yearlyAmounts[yr] || 0), 0);
  };

  const processData = () => {
    const filteredData = data.filter(item => getValueForItem(item) > 0);

    const categoryColors: { [key: string]: string } = {};
    categories.forEach(cat => { categoryColors[cat.id] = cat.color; });

    const categoryGroups = filteredData.reduce((acc, item) => {
      if (!acc[item.category]) acc[item.category] = [];
      acc[item.category].push(item);
      return acc;
    }, {} as { [category: string]: BudgetItem[] });

    const categoryOrbs: OrbData[] = Object.entries(categoryGroups).map(([categoryId, projects]) => {
      const totalValue = projects.reduce((sum, p) => sum + getValueForItem(p), 0);
      const categoryInfo = categories.find(cat => cat.id === categoryId);
      const displayName = categoryInfo ? categoryInfo.name :
        categoryId.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      return {
        id: `category-${categoryId}`,
        name: displayName,
        value: totalValue,
        category: categoryId,
        color: categoryColors[categoryId] || '#64748b',
        x: 0, y: 0, radius: 0, level: 0,
        isCategory: true,
        projects
      };
    });

    // Drill-down: only projects belonging to selectedCategory
    const drillSource = selectedCategory ? (categoryGroups[selectedCategory] || []) : [];
    const projectOrbs: OrbData[] = drillSource.map(item => {
      const catPos = categoryPositionsRef.current.get(item.category);
      return {
        id: item.id,
        name: item.name,
        value: getValueForItem(item),
        category: item.category,
        color: categoryColors[item.category] || '#64748b',
        x: catPos?.x ?? width / 2,
        y: catPos?.y ?? height / 2,
        radius: 0, level: 1,
        isCategory: false
      };
    });

    // All projects (ungrouped view) — start at category positions for explosion
    const allProjectOrbs: OrbData[] = filteredData.map(item => {
      const catPos = categoryPositionsRef.current.get(item.category);
      return {
        id: item.id,
        name: item.name,
        value: getValueForItem(item),
        category: item.category,
        color: categoryColors[item.category] || '#64748b',
        x: catPos?.x ?? width / 2,
        y: catPos?.y ?? height / 2,
        radius: 0, level: 0,
        isCategory: false
      };
    });

    return { categoryOrbs, projectOrbs, allProjectOrbs };
  };

  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const { categoryOrbs, projectOrbs, allProjectOrbs } = processData();

    let currentOrbs: OrbData[];
    if (viewMode === 'grouped') currentOrbs = categoryOrbs;
    else if (viewMode === 'projects') currentOrbs = projectOrbs;
    else currentOrbs = allProjectOrbs;

    if (currentOrbs.length === 0) return;

    const zoomContainer = svg.append('g').attr('class', 'zoom-container');
    zoomContainer.attr('transform', zoomRef.current.toString());

    // ── UI Buttons ────────────────────────────────────────────────────────────
    const toggleWidth = 140;

    const toggleButton = svg.append('g')
      .attr('class', 'toggle-button')
      .style('cursor', 'pointer')
      .on('click', () => {
        zoomRef.current = d3.zoomIdentity;
        setSelectedCategory(undefined);
        setViewMode(prev => prev === 'ungrouped' ? 'grouped' : 'ungrouped');
      });

    toggleButton.append('rect')
      .attr('x', width - toggleWidth - 10).attr('y', 10)
      .attr('width', toggleWidth).attr('height', 35).attr('rx', 8)
      .attr('fill', viewMode === 'ungrouped' ? '#059669' : '#4f46e5')
      .attr('stroke', '#fff').attr('stroke-width', 2);

    toggleButton.append('text')
      .attr('x', width - toggleWidth / 2 - 10).attr('y', 32)
      .attr('text-anchor', 'middle')
      .style('fill', 'white').style('font-size', '12px').style('font-weight', 'bold')
      .text(viewMode === 'ungrouped' ? '🔍 All Projects' : '📊 Grouped View');

    const resetButton = svg.append('g')
      .attr('class', 'reset-zoom-button')
      .style('cursor', 'pointer');

    resetButton.append('rect')
      .attr('x', width - toggleWidth - 10).attr('y', 52)
      .attr('width', toggleWidth).attr('height', 28).attr('rx', 6)
      .attr('fill', '#6b7280').attr('stroke', '#fff').attr('stroke-width', 1.5);

    resetButton.append('text')
      .attr('x', width - toggleWidth / 2 - 10).attr('y', 71)
      .attr('text-anchor', 'middle')
      .style('fill', 'white').style('font-size', '11px').style('font-weight', 'bold')
      .text('🔍 Reset Zoom');

    // Back button for drill-down view
    if (viewMode === 'projects') {
      const backButton = svg.append('g')
        .attr('class', 'back-button')
        .style('cursor', 'pointer')
        .on('click', () => {
          zoomRef.current = d3.zoomIdentity;
          setSelectedCategory(undefined);
          setViewMode('grouped');
        });

      backButton.append('rect')
        .attr('x', 10).attr('y', 10)
        .attr('width', 100).attr('height', 35).attr('rx', 8)
        .attr('fill', '#4f46e5').attr('stroke', '#fff').attr('stroke-width', 2);

      backButton.append('text')
        .attr('x', 60).attr('y', 32)
        .attr('text-anchor', 'middle')
        .style('fill', 'white').style('font-size', '12px').style('font-weight', 'bold')
        .text('← Back');
    }

    // ── Radius scale ──────────────────────────────────────────────────────────
    const scaleSource = (viewMode === 'projects' || viewMode === 'ungrouped') ? allProjectOrbs : categoryOrbs;
    const maxValue = d3.max(scaleSource, d => d.value) || 1;
    const minValue = d3.min(scaleSource, d => d.value) || 0;

    const radiusScale = d3.scaleSqrt()
      .domain([minValue, maxValue])
      .range([15, 120]);

    currentOrbs.forEach(d => { d.radius = radiusScale(d.value); });

    // ── Force simulation ──────────────────────────────────────────────────────
    let simulation: d3.Simulation<d3.SimulationNodeDatum, undefined>;

    if (viewMode === 'ungrouped') {
      const uniqueCategories = Array.from(new Set(currentOrbs.map(d => d.category)));
      const cols = Math.ceil(Math.sqrt(uniqueCategories.length));
      const spacing = Math.min(width, height) / (cols + 1);
      const clusterCenters: { [cat: string]: { x: number; y: number } } = {};
      uniqueCategories.forEach((category, i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        clusterCenters[category] = {
          x: (col + 1) * spacing + (width - cols * spacing) / 2,
          y: (row + 1) * spacing + (height - Math.ceil(uniqueCategories.length / cols) * spacing) / 2
        };
      });

      simulation = d3.forceSimulation(currentOrbs as any)
        .force('charge', d3.forceManyBody().strength(-30))
        .force('collision', d3.forceCollide().radius((d: any) => d.radius + 6).iterations(3))
        .force('clusterX', d3.forceX((d: any) => clusterCenters[d.category]?.x ?? width / 2).strength(0.6))
        .force('clusterY', d3.forceY((d: any) => clusterCenters[d.category]?.y ?? height / 2).strength(0.6))
        .force('center', d3.forceCenter(width / 2, height / 2).strength(0.02));
    } else {
      // Grouped or drill-down: simple centred layout
      simulation = d3.forceSimulation(currentOrbs as any)
        .force('charge', d3.forceManyBody().strength(-80))
        .force('center', d3.forceCenter(width / 2, height / 2))
        .force('collision', d3.forceCollide().radius((d: any) => d.radius + 5))
        .force('x', d3.forceX(width / 2).strength(0.3))
        .force('y', d3.forceY(height / 2).strength(0.3));
    }

    // ── wrapText ──────────────────────────────────────────────────────────────
    const wrapText = (d: OrbData): { lines: string[]; fontSize: number } => {
      const LINE_HEIGHT = 1.25;
      const CHAR_WIDTH  = 0.65;
      const V_PADDING   = 0.78;
      const H_PADDING   = 0.62;

      const usableW = d.radius * 2 * H_PADDING;
      const usableH = d.radius * 2 * V_PADDING;

      const words = d.name.split(' ');
      const longestWordLen = Math.max(...words.map(w => w.length));

      let fontSize = Math.min(
        Math.max(d.radius * 0.38, 7),
        usableW / (longestWordLen * CHAR_WIDTH)
      );

      for (let attempt = 0; attempt < 8; attempt++) {
        const charsPerLine = Math.max(Math.floor(usableW / (fontSize * CHAR_WIDTH)), 3);
        const lines: string[] = [];
        let current = '';
        for (const word of words) {
          const test = current ? `${current} ${word}` : word;
          if (test.length <= charsPerLine) {
            current = test;
          } else {
            if (current) lines.push(current);
            current = word;
          }
        }
        if (current) lines.push(current);

        const showValue = true;
        const totalLines = lines.length + (showValue ? 1 : 0);
        const totalH = totalLines * fontSize * LINE_HEIGHT;

        if (totalH <= usableH) return { lines, fontSize };
        fontSize *= 0.85;
      }

      return { lines: d.name.split(' '), fontSize: Math.max(fontSize, 6) };
    };

    // ── ClipPaths ─────────────────────────────────────────────────────────────
    const defs = svg.append('defs');
    defs.selectAll('clipPath.orb-clip')
      .data(currentOrbs).enter()
      .append('clipPath')
      .attr('class', 'orb-clip')
      .attr('id', d => `orb-clip-${d.id.replace(/[^a-z0-9]/gi, '-')}`)
      .append('circle')
      .attr('r', d => Math.max(d.radius - 5, 0));

    // ── Orbs ──────────────────────────────────────────────────────────────────
    const orbs = zoomContainer.selectAll('g.orb-group')
      .data(currentOrbs).enter()
      .append('g').attr('class', 'orb-group');

    orbs.append('circle')
      .attr('r', d => d.radius)
      .attr('fill', d => d.color)
      .attr('stroke', '#fff')
      .attr('stroke-width', 3)
      .attr('opacity', 0.85)
      .style('cursor', 'pointer')
      .on('click', (event, d) => {
        event.stopPropagation();
        if (d.isCategory && viewMode === 'grouped') {
          zoomRef.current = d3.zoomIdentity;
          setSelectedCategory(d.category);
          setViewMode('projects');
        } else if (!d.isCategory) {
          onOrbClick(d);
        }
      })
      .on('mouseover', function() { d3.select(this).attr('opacity', 1).attr('stroke-width', 4); })
      .on('mouseout', function() { d3.select(this).attr('opacity', 0.85).attr('stroke-width', 3); });

    // Text — clipped inside each orb
    orbs.each(function(d) {
      const { lines, fontSize } = wrapText(d);
      const showValue = true;
      const LINE_HEIGHT = 1.25;
      const totalLines = lines.length + (showValue ? 1 : 0);
      const blockH = totalLines * fontSize * LINE_HEIGHT;
      const startY = -blockH / 2 + fontSize * 0.85;

      const g = d3.select(this).append('g')
        .attr('clip-path', `url(#orb-clip-${d.id.replace(/[^a-z0-9]/gi, '-')})`)
        .style('pointer-events', 'none');

      lines.forEach((line, i) => {
        g.append('text')
          .attr('text-anchor', 'middle')
          .attr('y', startY + i * fontSize * LINE_HEIGHT)
          .style('fill', 'white')
          .style('font-size', `${fontSize}px`)
          .style('font-weight', 'bold')
          .style('text-shadow', '0 0 3px rgba(0,0,0,0.6)')
          .text(line);
      });

      if (showValue) {
        const valueFontSize = Math.min(fontSize * 0.85, 11);
        g.append('text')
          .attr('text-anchor', 'middle')
          .attr('y', startY + lines.length * fontSize * LINE_HEIGHT)
          .style('fill', 'white')
          .style('font-size', `${valueFontSize}px`)
          .style('font-weight', '600')
          .style('opacity', '0.9')
          .style('text-shadow', '0 0 2px rgba(0,0,0,0.5)')
          .text(`$${(d.value / 1_000_000).toFixed(2)}M`);
      }
    });

    // ── Tick handler ──────────────────────────────────────────────────────────
    simulation.on('tick', () => {
      orbs.attr('transform', d => `translate(${d.x || 0},${d.y || 0})`);
      if (viewMode === 'grouped') {
        currentOrbs.forEach(d => {
          categoryPositionsRef.current.set(d.category, { x: d.x || 0, y: d.y || 0 });
        });
      }
    });

    simulationRef.current = simulation;

    // ── Auto-fit for projects and ungrouped views ─────────────────────────────
    let initialTransform: d3.ZoomTransform = zoomRef.current;

    if (viewMode === 'projects' || viewMode === 'ungrouped') {
      simulation.tick(300);
      orbs.attr('transform', d => `translate(${d.x || 0},${d.y || 0})`);

      const pad = 40;
      const x0 = (d3.min(currentOrbs, d => (d.x || 0) - d.radius) ?? 0) - pad;
      const x1 = (d3.max(currentOrbs, d => (d.x || 0) + d.radius) ?? width) + pad;
      const y0 = (d3.min(currentOrbs, d => (d.y || 0) - d.radius) ?? 0) - pad;
      const y1 = (d3.max(currentOrbs, d => (d.y || 0) + d.radius) ?? height) + pad;

      const scale = Math.min(width / (x1 - x0), height / (y1 - y0));
      const tx = (width - scale * (x0 + x1)) / 2;
      const ty = (height - scale * (y0 + y1)) / 2;
      initialTransform = d3.zoomIdentity.translate(tx, ty).scale(scale);
      zoomRef.current = initialTransform;
      zoomContainer.attr('transform', initialTransform.toString());
    }

    // ── Zoom & pan ────────────────────────────────────────────────────────────
    const zoom: d3.ZoomBehavior<SVGSVGElement, unknown> = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 8])
      .on('zoom', event => {
        zoomRef.current = event.transform;
        zoomContainer.attr('transform', event.transform.toString());
      });

    svg.call(zoom);
    svg.call(zoom.transform, initialTransform);

    resetButton.on('click', () => {
      zoomRef.current = initialTransform;
      svg.transition().duration(300).call(zoom.transform, initialTransform);
    });

    return () => {
      if (simulationRef.current) simulationRef.current.stop();
      svg.on('.zoom', null);
    };
  }, [data, selectedYears, width, height, viewMode, selectedCategory]);

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      style={{ background: '#f8fafc', borderRadius: '8px' }}
    />
  );
};
